import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Input,
  Space,
  message,
  Tag,
  Tooltip,
  Popconfirm,
  Avatar,
  Typography,
  Form,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import courseService from "../../services/course.service";
import AuthService from "../../services/auth.service";

const { Text } = Typography;

const UserManagement = () => {
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [instructorsData, studentsData] = await Promise.all([
        courseService.getAllInstructors(),
        courseService.getAllStudents(),
      ]);
      setInstructors(instructorsData);
      setStudents(studentsData);
    } catch (error) {
      message.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActivation = async (userId) => {
    try {
      await courseService.toggleUserActivation(userId);
      message.success("Statut mis à jour avec succès");
      loadUsers();
    } catch (error) {
      message.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleDelete = async (userId) => {
    try {
      await courseService.deleteUser(userId);
      message.success("Utilisateur supprimé avec succès");
      loadUsers();
    } catch (error) {
      message.error("Erreur lors de la suppression");
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    form.resetFields();
    setResetPasswordVisible(true);
  };

  const handleResetPasswordSubmit = async () => {
    try {
      const values = await form.validateFields();
      await AuthService.resetUserPassword(selectedUser.id, values.newPassword);
      message.success("Mot de passe réinitialisé avec succès");
      setResetPasswordVisible(false);
      setSelectedUser(null);
      form.resetFields();
    } catch (error) {
      if (error.errorFields) {
        // Erreur de validation du formulaire
        return;
      }
      message.error("Erreur lors de la réinitialisation du mot de passe");
    }
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Veuillez saisir un mot de passe"));
    }
    if (value.length < 6) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins 6 caractères")
      );
    }
    if (!/(?=.*[a-z])/.test(value)) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins une minuscule")
      );
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins une majuscule")
      );
    }
    if (!/(?=.*\d)/.test(value)) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins un chiffre")
      );
    }
    return Promise.resolve();
  };

  const columns = [
    {
      title: "Utilisateur",
      key: "user",
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar
            style={{
              backgroundColor: record.isActivated ? "#52c41a" : "#ff4d4f",
            }}
            icon={<UserOutlined />}
          />
          <div>
            <div style={{ fontWeight: "bold" }}>{record.username}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Rôles",
      dataIndex: "roles",
      key: "roles",
      width: 200,
      render: (roles) => (
        <>
          {roles.map((role) => {
            let color = "blue";
            if (role === "Student") color = "green";
            if (role === "Instructor") color = "purple";
            if (role === "Admin") color = "red";

            return (
              <Tag key={role} color={color}>
                {role}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: "Statut",
      key: "status",
      width: 200,
      render: (_, record) => (
        <div>
          <Tag color={record.isActivated ? "green" : "red"}>
            {record.isActivated ? "Actif" : "Inactif"}
          </Tag>
          <Tag color={record.isEmailConfirmed ? "green" : "orange"}>
            {record.isEmailConfirmed ? "Email confirmé" : "Non confirmé"}
          </Tag>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Réinitialiser le mot de passe">
            <Button
              type="default"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleResetPassword(record)}
            >
              Mot de passe
            </Button>
          </Tooltip>
          <Tooltip title={record.isActivated ? "Désactiver" : "Activer"}>
            <Button
              type={record.isActivated ? "default" : "primary"}
              size="small"
              onClick={() => handleToggleActivation(record.id)}
            >
              {record.isActivated ? "Désactiver" : "Activer"}
            </Button>
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cet utilisateur ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 16px" }}>
        <Space>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={loadUsers}
            loading={loading}
          >
            Actualiser
          </Button>
        </Space>
      </Card>

      <Card
        title="Instructeurs"
        bordered={false}
        style={{ marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Table
          columns={columns}
          dataSource={instructors}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Card
        title="Étudiants"
        bordered={false}
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Table
          columns={columns}
          dataSource={students}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={
          <div>
            <KeyOutlined style={{ marginRight: 8, color: "#667eea" }} />
            Réinitialiser le mot de passe
          </div>
        }
        open={resetPasswordVisible}
        onOk={handleResetPasswordSubmit}
        onCancel={() => {
          setResetPasswordVisible(false);
          setSelectedUser(null);
          form.resetFields();
        }}
        okText="Réinitialiser"
        cancelText="Annuler"
      >
        {selectedUser && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Utilisateur : </Text>
            <Text>{selectedUser.username}</Text>
            <br />
            <Text strong>Email : </Text>
            <Text>{selectedUser.email}</Text>
          </div>
        )}

        <Form form={form} layout="vertical">
          <Form.Item
            label="Nouveau mot de passe"
            name="newPassword"
            rules={[
              {
                required: true,
                message: "Veuillez saisir un nouveau mot de passe",
              },
              { validator: validatePassword },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#667eea" }} />}
              placeholder="Saisissez le nouveau mot de passe"
              size="large"
            />
          </Form.Item>

          <div
            style={{
              background: "#f0f5ff",
              padding: "12px",
              borderRadius: "8px",
            }}
          >
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Règles du mot de passe :
            </Text>
            <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "12px" }}>
              <li>Au moins 6 caractères</li>
              <li>Au moins une lettre minuscule (a-z)</li>
              <li>Au moins une lettre majuscule (A-Z)</li>
              <li>Au moins un chiffre (0-9)</li>
            </ul>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;